import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { ANCORAS, MARCA } from '@/features/landing/conteudo';
import { ThemeToggle } from '@/shared/components/ThemeToggle';
import { ROTAS } from '@/shared/lib/rotas';
import { Button } from '@/shared/ui/button';

/**
 * Header da landing: nav de âncoras + porta de entrada da conta.
 *
 * O par de CTAs troca por "Abrir plataforma" quando já há sessão. Sem isso, o
 * usuário logado que digita o domínio na barra cai numa página que o convida a
 * criar a conta que ele já tem.
 *
 * Enquanto `carregando`, mostra o estado anônimo: é o caso da esmagadora
 * maioria de quem chega aqui, e piscar "Abrir plataforma" para quem não tem
 * conta seria o erro mais caro dos dois.
 */
export function LandingHeader() {
  const { autenticado, carregando } = useAuth();
  const [menuAberto, setMenuAberto] = useState(false);
  const logado = autenticado && !carregando;

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex h-20 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
        {/* ⚠️ As duas alturas são DIFERENTES de propósito. Os dois arquivos têm
            enquadramentos distintos: `logo-nohra-light.png` é um quadrado
            2000×2000 em que a marca ocupa só a faixa do meio (~37% da altura),
            e `logo-nohra-dark.png` é 2617×1472 com a marca ocupando ~71%. Com
            a mesma classe nos dois, o logo do tema light sai pela metade do
            tamanho do outro. h-20/h-11 é o par que deixa a marca visível do
            mesmo tamanho nos dois temas. */}
        <Link to={ROTAS.home} className="shrink-0" aria-label={MARCA.nome}>
          <img
            src="/logo-nohra-light.png"
            alt={MARCA.nome}
            className="h-20 w-auto object-contain dark:hidden"
          />
          <img
            src="/logo-nohra-dark.png"
            alt={MARCA.nome}
            className="hidden h-11 w-auto object-contain dark:block"
          />
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          {ANCORAS.map((ancora) => (
            <a
              key={ancora.id}
              href={`#${ancora.id}`}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {ancora.rotulo}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle />

          {logado ? (
            <Button asChild size="sm" className="bg-brand text-brand-foreground hover:bg-brand/90">
              <Link to={ROTAS.dashboard}>Abrir plataforma</Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
                <Link to={ROTAS.login}>Entrar</Link>
              </Button>
              <Button asChild size="sm" className="bg-brand text-brand-foreground hover:bg-brand/90">
                <Link to={ROTAS.registro}>Criar conta</Link>
              </Button>
            </>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-expanded={menuAberto}
            aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
            onClick={() => setMenuAberto((aberto) => !aberto)}
          >
            {menuAberto ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {menuAberto && (
        <nav className="border-t border-border/60 bg-background px-4 py-3 md:hidden">
          {ANCORAS.map((ancora) => (
            <a
              key={ancora.id}
              href={`#${ancora.id}`}
              onClick={() => setMenuAberto(false)}
              className="block rounded-lg px-2 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {ancora.rotulo}
            </a>
          ))}
          {!logado && (
            <Link
              to={ROTAS.login}
              className="block rounded-lg px-2 py-2.5 text-sm font-medium text-brand sm:hidden"
            >
              Entrar
            </Link>
          )}
        </nav>
      )}
    </header>
  );
}
